// @flow
import * as React from 'react';

import { selectCartUUID } from '@gandi/cart';
import { useTranslator } from '@gandi/translate';

import type { NavEntry } from '@gandi/ui/layout';

import { useAppSelector } from 'src/hooks';
import { useFeatureFlag } from 'src/hooks/useFeatureFlag';
import {
  selectGostackURL,
  selectCertificateURL,
  getLinkTarget,
} from 'src/modules/config';

/** Close the Hosting menu even when staying on the same page's pathname */
function handleItemClick() {
  const escapeEvent = new KeyboardEvent('keydown', { key: 'Escape' });
  document.dispatchEvent(escapeEvent);
}

export function useNavigationMenus(): Array<NavEntry> {
  const { __ } = useTranslator();
  const cartID = useAppSelector(selectCartUUID);
  const gostackUrl = useAppSelector(selectGostackURL);
  const certificateUrl = useAppSelector(selectCertificateURL);

  const FF_SHOP_ACME = useFeatureFlag('FF_SHOP_ACME');

  return React.useMemo(() => {
    const cartSubPath = cartID ? `/${cartID}` : '';

    const menus: Array<NavEntry> = [
      {
        key: 'domains',
        label: __('Domains'),
        items: [
          {
            children: __('Buy domains'),
            to: `/domain/suggest${cartSubPath}`,
            title: __('Buy domains'),
          },
          {
            children: __('Renew domains'),
            to: `/domain/renew${cartSubPath}`,
            title: __('Renew domains'),
          },
          {
            children: __('Transfer domains'),
            to: `/domain/transfer${cartSubPath}`,
            title: __('Transfer domains'),
          },
          {
            children: __('Register your trademark'),
            to: '/tmch',
            title: __('TMCH Trademark Registration'),
          },
        ],
      },
      {
        key: 'hosting',
        label: __('Hosting'),
        items: [
          {
            children: __('Buy web hosting'),
            to: `/simplehosting/create${cartSubPath}`,
            title: __('Buy web hosting'),
            onClick: handleItemClick,
          },
          {
            children: __('Buy Wordpress hosting'),
            to: `/simplehosting/create${cartSubPath}?type=wordpress_v2`,
            title: __('Buy Wordpress hosting'),
            onClick: handleItemClick,
          },
          {
            children: __(
              '<for-translators-use>Menu item for cloud create page</for-translators-use>Buy cloud servers',
            ),
            href: gostackUrl,
            title: __('Buy cloud servers'),
          },
        ],
      },
    ];

    if (FF_SHOP_ACME) {
      menus.push({
        key: 'certificates',
        label: __('Certificates'),
        items: [
          {
            children: __('ACME certificates'),
            to: `/certificate/acme${cartSubPath}`,
            title: __('Buy ACME certificate'),
          },
          {
            ...getLinkTarget(certificateUrl),
            children: __('SSL Certificates'),
            title: __('Buy SSL certificate'),
          },
        ],
      });
    } else {
      menus.push({
        ...getLinkTarget(certificateUrl),
        key: 'ssl',
        label: __('SSL Certificates'),
        title: __('Buy SSL certificate'),
      });
    }

    menus.push({
      key: 'mailboxes',
      label: __('Custom Mailboxes'),
      to: '/mailboxes/create',
      title: __('Buy mailboxes for your domain.'),
    });

    return menus;
  }, [__, cartID, certificateUrl, FF_SHOP_ACME, gostackUrl]);
}
